import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiurl } from '../../utils/constants';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx-js-style';

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MONTH_ABBR = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function getMonthColumns(startDate, endDate, monthlyRows = []) {
	const now = new Date();
	const toIndex = (year, month) => year * 12 + (month - 1);
	const fromIndex = (index) => [Math.floor(index / 12), (index % 12) + 1];

	let startYear;
	let startMonth;
	let endYear;
	let endMonth;

	const dataIndexes = [];
	monthlyRows.forEach((row) => {
		Object.keys(row.values).forEach((key) => {
			const [y, m] = key.split('-').map(Number);
			dataIndexes.push(toIndex(y, m));
		});
	});
	const nowIndex = toIndex(now.getFullYear(), now.getMonth() + 1);

	if (startDate) {
		[startYear, startMonth] = startDate.split('-').map(Number);
		if (endDate) {
			[endYear, endMonth] = endDate.split('-').map(Number);
		} else {
			const maxIndex = Math.max(nowIndex, ...dataIndexes, toIndex(startYear, startMonth));
			[endYear, endMonth] = fromIndex(maxIndex);
		}
	} else if (endDate) {
		[endYear, endMonth] = endDate.split('-').map(Number);
		const yearStartIndex = toIndex(endYear, 1);
		const minIndex = Math.min(yearStartIndex, ...dataIndexes, toIndex(endYear, endMonth));
		[startYear, startMonth] = fromIndex(minIndex);
	} else {
		const yearStartIndex = toIndex(now.getFullYear(), 1);
		const maxIndex = Math.max(nowIndex, ...dataIndexes);
		const minIndex = Math.min(yearStartIndex, ...dataIndexes);

		[startYear, startMonth] = fromIndex(minIndex);
		[endYear, endMonth] = fromIndex(maxIndex);
	}

	const columns = [];
	let year = startYear;
	let month = startMonth;
	while (year < endYear || (year === endYear && month <= endMonth)) {
		columns.push({ year, month, key: `${year}-${month}`, label: MONTH_NAMES[month - 1] });
		month += 1;
		if (month > 12) {
			month = 1;
			year += 1;
		}
	}
	return columns;
}

function ListBudg() {
	const [monthlyRows, setMonthlyRows] = useState([]);
	const [ocByAccount, setOcByAccount] = useState({});
	const [smByAccount, setSmByAccount] = useState({});
	const [ocByAccountMonth, setOcByAccountMonth] = useState({});
	const [smByAccountMonth, setSmByAccountMonth] = useState({});
	const [globalMonthly, setGlobalMonthly] = useState({});
	const [typeAccum, setTypeAccum] = useState('Acumulado');
	const [listCC, setListCC] = useState([]);
	const [listAcco, setListAcco] = useState([]);
	const [filtCC, setFiltCC] = useState('');
	const [filtCuenta, setFiltCuenta] = useState('');
	const [startDate, setStartDate] = useState(() => {
		const now = new Date();
		return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
	});
	const [endDate, setEndDate] = useState('');
	const [order, setOrder] = useState('asc');
	const [orderBy, setOrderBy] = useState('cuenta');
	const { mail, iden, role, budg } = useSelector((state) => state.user);
	const isPrivileged = Number(role) >= 3 || Boolean(budg);

	const filtCCCode = listCC.find((cc) => cc.name === filtCC || String(cc.code) === filtCC)?.code;
	const filtCuentaCode = listAcco.find((a) => a.name === filtCuenta || String(a.code) === filtCuenta)?.code;

	const buildWhereCC = useCallback(() => {
		const conditions = [];

		if (startDate) {
			const [startYear, startMonth] = startDate.split('-').map(Number);
			conditions.push(`(b.year > ${startYear} OR (b.year = ${startYear} AND b.mont >= ${startMonth}))`);
		}
		if (endDate) {
			const [baseEndYear, baseEndMonth] = endDate.split('-').map(Number);

			let endYear = baseEndYear;
			let endMonth = baseEndMonth + 1;
			if (endMonth > 12) {
				endMonth = 1;
				endYear += 1;
			}

			conditions.push(`(b.year < ${endYear} OR (b.year = ${endYear} AND b.mont < ${endMonth}))`);
		}
		if (filtCCCode) conditions.push(`b.cent = ${Number(filtCCCode)}`);
		if (!isPrivileged) conditions.push(`b.cent IN (SELECT code FROM req__cent WHERE bill = ${Number(iden)})`);
		return conditions.length ? `WHERE ${conditions.join(' AND ')}` : '';
	}, [startDate, endDate, filtCCCode, isPrivileged, iden]);

	const loadBudgMonthly = useCallback(async () => {
		if (!mail) return;

		try {
			const whereCC = buildWhereCC();

			const response = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: `SELECT q.acco,
								COALESCE(q.name, q.acco) AS cuenta,
								q.year,
								q.mont,
								SUM(q.budg) AS ppto
							FROM (
								SELECT b.*, a.name
								FROM inv__budg b
								JOIN inv__acco a ON b.acco = a.code
								${whereCC}
							) q
							GROUP BY q.acco, q.name, q.year, q.mont
							ORDER BY q.acco ASC`
				})
			});

			if (!response.ok) {
				console.error('Error cargando presupuesto');
				setMonthlyRows([]);
				return;
			}

			const data = await response.json();
			const byAccount = new Map();

			(Array.isArray(data) ? data : []).forEach((item) => {
				const cuentaName = String(item?.cuenta ?? item?.acco ?? '');
				const key = `${item.year}-${item.mont}`;

				if (!byAccount.has(cuentaName)) byAccount.set(cuentaName, { acco: item?.acco, values: {} });
				byAccount.get(cuentaName).values[key] = Number(item?.ppto || 0);
			});

			const result = Array.from(byAccount.entries()).map(([cuenta, { acco, values }]) => ({ cuenta, acco, values }));
			setMonthlyRows(result);
		} catch (error) {
			console.error('Error en loadBudgMonthly:', error);
			setMonthlyRows([]);
		}
	}, [mail, buildWhereCC]);

	const buildOcDateCondition = useCallback((column) => {
		if (!startDate && !endDate) return '';

		const conditions = [];

		if (startDate) {
			const [startYear, startMonth] = startDate.split('-').map(Number);
			const fromDate = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
			conditions.push(`${column} >= '${fromDate}'`);
		}

		if (endDate) {
			const [endYear, endMonth] = endDate.split('-').map(Number);
			let toYear = endYear;
			let toMonth = endMonth + 1;
			if (toMonth > 12) {
				toMonth = 1;
				toYear += 1;
			}
			const toDate = `${toYear}-${String(toMonth).padStart(2, '0')}-01`;
			conditions.push(`${column} < '${toDate}'`);
		}

		return `AND ${conditions.join(' AND ')}`;
	}, [startDate, endDate]);

	const loadOcSums = useCallback(async () => {
		if (!mail) return;

		try {
			const dateCondition = buildOcDateCondition('o.sent');
			// CC de la línea de la OC en SAP; si no viene, el de la solicitud
			const lineCent = `COALESCE(l.CostingCode, s.cent)`;
			const centConditions = [];
			if (filtCCCode) centConditions.push(`AND ${lineCent} = ${Number(filtCCCode)}`);
			if (!isPrivileged) centConditions.push(`AND ${lineCent} IN (SELECT code FROM req__cent WHERE bill = ${Number(iden)})`);

			// Monto de cada línea de la OC tal como viene de SAP (ord__line.cost = LineTotal)
			// Cuenta: artículos, la de su grupo (en SAP vienen a EXISTENCIAS); servicios, la de la línea (ord__line.AccountCode)
			// La cuenta se toma como está escrita en inv__budg, para que la clave calce con las filas del presupuesto
			const lineAcco = `CASE WHEN l.item REGEXP '^[A-Za-z]{2}' THEN l.AccountCode ELSE COALESCE(g.acco, l.AccountCode) END`;
			const response = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: `SELECT
								COALESCE(b.acco, ${lineAcco}) AS acco,
								YEAR(o.sent) AS year,
								MONTH(o.sent) AS mont,
								SUM(l.cost) AS oc
							FROM ord__form o
							JOIN ord__line l
								ON l.Entry = o.orde
							LEFT JOIN inv__item i
								ON i.code = l.item
							LEFT JOIN inv__grou g
								ON g.numb = i.grou
							LEFT JOIN (SELECT DISTINCT acco FROM inv__budg) b
								ON b.acco = ${lineAcco}
							LEFT JOIN req__soli s
								ON s.entry = o.entr
							WHERE o.canc = 0
							  ${dateCondition}
							  ${centConditions.join(' ')}
							GROUP BY COALESCE(b.acco, ${lineAcco}), YEAR(o.sent), MONTH(o.sent)
							ORDER BY acco`
				})
			});

			if (!response.ok) {
				console.error('Error cargando suma de OC');
				setOcByAccount({});
				setOcByAccountMonth({});
				return;
			}

			const data = await response.json();
			const map = {};
			const monthMap = {};

			(Array.isArray(data) ? data : []).forEach((row) => {
				if (row?.acco === null || row?.acco === undefined) return;
				const acco = String(row.acco);
				const value = Number(row?.oc || 0);
				map[acco] = (map[acco] || 0) + value;

				if (!monthMap[acco]) monthMap[acco] = {};
				monthMap[acco][`${row.year}-${row.mont}`] = value;
			});

			setOcByAccount(map);
			setOcByAccountMonth(monthMap);
		} catch (error) {
			console.error('Error en loadOcSums:', error);
			setOcByAccount({});
			setOcByAccountMonth({});
		}
	}, [mail, buildOcDateCondition, filtCCCode, isPrivileged, iden]);

	const loadSmSums = useCallback(async () => {
		if (!mail) return;

		try {
			const dateCondition = buildOcDateCondition('e.sent');
			const centConditions = [];
			if (filtCCCode) centConditions.push(`AND s.cent = ${Number(filtCCCode)}`);
			if (!isPrivileged) centConditions.push(`AND s.cent IN (SELECT code FROM req__cent WHERE bill = ${Number(iden)})`);

			const response = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: `SELECT
								CASE WHEN m.item REGEXP '^[A-Za-z]{2}' THEN st.acco ELSE g.acco END AS acco,
								a.name AS cuenta,
								YEAR(e.sent) AS year,
								MONTH(e.sent) AS mont,
								SUM(m.move * i.cost) AS sm
							FROM req__exit e
							JOIN req__move m
								ON m.type = 'exit'
							   AND m.docu = e.docu
							JOIN inv__item i
								ON i.code = m.item
							LEFT JOIN inv__grou g
								ON g.numb = i.grou
							LEFT JOIN inv__stor st
								ON st.item = m.item
							   AND st.ware = i.ware
							LEFT JOIN inv__acco a
								ON a.code = CASE WHEN m.item REGEXP '^[A-Za-z]{2}' THEN st.acco ELSE g.acco END
							LEFT JOIN req__soli s
								ON s.iden = e.soli
							WHERE e.stat = 'o'
							  ${dateCondition}
							  ${centConditions.join(' ')}
							GROUP BY acco, cuenta, year, mont
							ORDER BY acco`
				})
			});

			if (!response.ok) {
				console.error('Error cargando suma de SM');
				setSmByAccount({});
				setSmByAccountMonth({});
				return;
			}

			const data = await response.json();
			const map = {};
			const monthMap = {};

			(Array.isArray(data) ? data : []).forEach((row) => {
				if (row?.acco === null || row?.acco === undefined) return;
				const acco = String(row.acco);
				const value = Number(row?.sm || 0);
				map[acco] = (map[acco] || 0) + value;

				if (!monthMap[acco]) monthMap[acco] = {};
				monthMap[acco][`${row.year}-${row.mont}`] = value;
			});

			setSmByAccount(map);
			setSmByAccountMonth(monthMap);
		} catch (error) {
			console.error('Error en loadSmSums:', error);
			setSmByAccount({});
			setSmByAccountMonth({});
		}
	}, [mail, buildOcDateCondition, filtCCCode, isPrivileged, iden]);

	const loadCC = useCallback(async () => {
		if (!mail) return;

		try {
			const where = isPrivileged ? 'AND 1' : `AND bill = ${Number(iden)}`;
			const response = await fetch(apiurl, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					table: `req__cent cent`,
					where,
					order: ` name`, user: mail
				})
			});

			if (response.ok) {
				const data = await response.json();
				const result = data.map((row) => ({
					code: row.code,
					name: row.name
				}));

				setListCC(result);
			} else {
				console.error('Error al obtener los centros de costo');
			}
		} catch (error) {
			console.error('Error en loadCC:', error);
		}
	}, [mail, iden, isPrivileged]);

	const loadListAcco = useCallback(async () => {
		if (!mail) return;

		try {
			const response = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: `SELECT code, name FROM inv__acco;`
				})
			});

			if (response.ok) {
				const data = await response.json();
				setListAcco(data.map((row) => ({ code: row.code, name: row.name })));
			} else {
				console.error('Error al obtener las cuentas');
			}
		} catch (error) {
			console.error('Error en loadListAcco:', error);
		}
	}, [mail]);

	const loadGlobalMonthly = useCallback(async () => {
		if (!mail) return;

		try {
			const whereCC = buildWhereCC();
			const ocDateCondition = buildOcDateCondition('o.sent');
			const smDateCondition = buildOcDateCondition('e.sent');
			const centConditions = [];
			if (filtCCCode) centConditions.push(`AND s.cent = ${Number(filtCCCode)}`);
			if (!isPrivileged) centConditions.push(`AND s.cent IN (SELECT code FROM req__cent WHERE bill = ${Number(iden)})`);
			// OC: CC de la línea en SAP; si no viene, el de la solicitud
			const lineCent = `COALESCE(l.CostingCode, s.cent)`;
			const ocCentConditions = [];
			if (filtCCCode) ocCentConditions.push(`AND ${lineCent} = ${Number(filtCCCode)}`);
			if (!isPrivileged) ocCentConditions.push(`AND ${lineCent} IN (SELECT code FROM req__cent WHERE bill = ${Number(iden)})`);

			const [budgRes, ocRes, smRes] = await Promise.all([
				fetch(apiurl + 'queryAux', {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						user: mail,
						query: `SELECT b.year, b.mont, SUM(b.budg) AS ppto FROM inv__budg b ${whereCC} GROUP BY b.year, b.mont`
					})
				}),
				fetch(apiurl + 'queryAux', {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						user: mail,
						query: `SELECT YEAR(o.sent) AS year, MONTH(o.sent) AS mont, SUM(l.cost) AS oc
									FROM ord__form o
									JOIN ord__line l ON l.Entry = o.orde
									LEFT JOIN req__soli s ON s.entry = o.entr
									WHERE o.canc = 0 ${ocDateCondition} ${ocCentConditions.join(' ')}
									GROUP BY YEAR(o.sent), MONTH(o.sent)`
					})
				}),
				fetch(apiurl + 'queryAux', {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						user: mail,
						query: `SELECT YEAR(e.sent) AS year, MONTH(e.sent) AS mont, SUM(m.move * i.cost) AS sm
									FROM req__exit e
									JOIN req__move m ON m.type = 'exit' AND m.docu = e.docu
									JOIN inv__item i ON i.code = m.item
									LEFT JOIN req__soli s ON s.iden = e.soli
									WHERE e.stat = 'o' ${smDateCondition} ${centConditions.join(' ')}
									GROUP BY YEAR(e.sent), MONTH(e.sent)`
					})
				})
			]);

			if (!budgRes.ok || !ocRes.ok || !smRes.ok) {
				console.error('Error cargando totales mensuales globales');
				setGlobalMonthly({});
				return;
			}

			const [budgData, ocData, smData] = await Promise.all([budgRes.json(), ocRes.json(), smRes.json()]);
			const map = {};

			(Array.isArray(budgData) ? budgData : []).forEach((row) => {
				const key = `${row.year}-${row.mont}`;
				if (!map[key]) map[key] = { ppto: 0, oc: 0, sm: 0 };
				map[key].ppto = Number(row?.ppto || 0);
			});
			(Array.isArray(ocData) ? ocData : []).forEach((row) => {
				const key = `${row.year}-${row.mont}`;
				if (!map[key]) map[key] = { ppto: 0, oc: 0, sm: 0 };
				map[key].oc = Number(row?.oc || 0);
			});
			(Array.isArray(smData) ? smData : []).forEach((row) => {
				const key = `${row.year}-${row.mont}`;
				if (!map[key]) map[key] = { ppto: 0, oc: 0, sm: 0 };
				map[key].sm = Number(row?.sm || 0);
			});

			setGlobalMonthly(map);
		} catch (error) {
			console.error('Error en loadGlobalMonthly:', error);
			setGlobalMonthly({});
		}
	}, [mail, buildWhereCC, buildOcDateCondition, filtCCCode, isPrivileged, iden]);

	useEffect(() => {
		loadBudgMonthly();
		loadOcSums();
		loadSmSums();
		loadCC();
		loadListAcco();
		loadGlobalMonthly();
	}, [loadBudgMonthly, loadOcSums, loadSmSums, loadCC, loadListAcco, loadGlobalMonthly]);

	const globalTotals = Object.values(globalMonthly).reduce((acc, v) => ({
		ppto: acc.ppto + Number(v?.ppto || 0),
		oc: acc.oc + Number(v?.oc || 0),
		sm: acc.sm + Number(v?.sm || 0)
	}), { ppto: 0, oc: 0, sm: 0 });

	const filteredMonthlyRows = monthlyRows.filter((row) =>
		!filtCuenta || (filtCuentaCode
			? String(row.acco) === String(filtCuentaCode)
			: (row.cuenta || '').toLowerCase().includes(filtCuenta.toLowerCase()) || String(row.acco ?? '').toLowerCase().includes(filtCuenta.toLowerCase()))
	);

	const filteredRows = filteredMonthlyRows.map((row) => {
		const ppto = Object.values(row.values).reduce((acc, val) => acc + Number(val || 0), 0);
		const oc = ocByAccount[String(row.acco)] || 0;
		const sm = smByAccount[String(row.acco)] || 0;
		return { cuenta: row.cuenta, ppto, oc, sm, diferencia: ppto - oc - sm };
	});

	const acumTotals = filtCuenta
		? filteredRows.reduce((acc, row) => ({
			ppto: acc.ppto + row.ppto,
			oc: acc.oc + row.oc,
			sm: acc.sm + row.sm
		}), { ppto: 0, oc: 0, sm: 0 })
		: globalTotals;

	const handleRequestSort = (property) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const sortComparator = (a, b) => {
		const valA = a[orderBy];
		const valB = b[orderBy];
		const comparison = typeof valA === 'string' ? valA.localeCompare(valB) : valA - valB;
		return order === 'asc' ? comparison : -comparison;
	};

	const sortedRows = [...filteredRows].sort(sortComparator);

	const monthColumns = getMonthColumns(startDate, endDate, filteredMonthlyRows);

	const monthlyRowsWithTotal = filteredMonthlyRows.map((row) => {
		const rowTotal = monthColumns.reduce((acc, col) => acc + Number(row.values[col.key] || 0), 0);
		return { ...row, rowTotal };
	});

	const monthlyTotals = monthColumns.map((col) =>
		monthlyRowsWithTotal.reduce((acc, row) => acc + Number(row.values[col.key] || 0), 0)
	);
	const monthlyGrandTotal = monthlyRowsWithTotal.reduce((acc, row) => acc + row.rowTotal, 0);

	const fNum = (value) => Number(value || 0);
	const fFmt = (value) => fNum(value).toLocaleString('es-CL');

	const styleHeaderAndTotalRow = (ws, colCount, totalRowIndex) => {
		for (let colIndex = 1; colIndex <= colCount; colIndex++) {
			const headerCellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex - 1 });
			if (!ws[headerCellRef]) continue;
			if (!ws[headerCellRef].s) ws[headerCellRef].s = {};
			ws[headerCellRef].s.font = { ...(ws[headerCellRef].s.font || {}), color: { rgb: '000000' } };
		}

		for (let colIndex = 1; colIndex <= colCount; colIndex++) {
			const cellRef = XLSX.utils.encode_cell({ r: totalRowIndex - 1, c: colIndex - 1 });
			if (!ws[cellRef]) continue;
			if (!ws[cellRef].s) ws[cellRef].s = {};
			ws[cellRef].s.font = {
				...(ws[cellRef].s.font || {}),
				bold: true,
				color: ws[cellRef].s.font?.color || { rgb: '000000' }
			};
		}
	};

	const downloadWorkbook = (ws) => {
		const wb = { Sheets: { presupuesto: ws }, SheetNames: ['presupuesto'] };
		const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
		const data = new Blob([excelBuffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
		});
		FileSaver.saveAs(data, 'presupuesto.xlsx');
	};

	const exportMensualExcel = () => {
		const headers = ['Cuenta', ...monthColumns.map((col) => MONTH_ABBR[col.month - 1]), 'TOTAL'];
		const dataRows = monthlyRowsWithTotal.map((row) => [row.cuenta, ...monthColumns.map((col) => Number(row.values[col.key] || 0)), row.rowTotal]);
		const totalRow = ['TOTAL', ...monthlyTotals, monthlyGrandTotal];
		const colCount = headers.length;
		const rowTotalColIndex = colCount - 1;

		const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows, totalRow]);
		ws['!cols'] = [{ wch: 38 }, ...Array(colCount - 1).fill({ wch: 14 })];

		const totalRowIndex = dataRows.length + 2;

		for (let rowIndex = 2; rowIndex <= totalRowIndex; rowIndex++) {
			for (let colIndex = 1; colIndex <= colCount; colIndex++) {
				const cellRef = XLSX.utils.encode_cell({ r: rowIndex - 1, c: colIndex - 1 });
				if (!ws[cellRef]) continue;
				if (!ws[cellRef].s) ws[cellRef].s = {};

				if (colIndex === 1) {
					ws[cellRef].s.font = { ...(ws[cellRef].s.font || {}), bold: true, color: { rgb: '000000' } };
				} else {
					ws[cellRef].z = '#,##0';
				}
			}
		}

		for (let rowIndex = 2; rowIndex < totalRowIndex; rowIndex++) {
			const cellRef = XLSX.utils.encode_cell({ r: rowIndex - 1, c: rowTotalColIndex });
			if (!ws[cellRef]) continue;

			const value = Number(ws[cellRef].v || 0);
			if (value === 0) {
				delete ws[cellRef];
				continue;
			}

			if (!ws[cellRef].s) ws[cellRef].s = {};
			ws[cellRef].s.font = {
				...(ws[cellRef].s.font || {}),
				bold: true,
				color: value < 0 ? { rgb: 'FF0000' } : { rgb: '000000' }
			};
		}

		styleHeaderAndTotalRow(ws, colCount, totalRowIndex);
		downloadWorkbook(ws);
	};

	const exportAcumuladoExcel = () => {
		const headers = ['Cuenta', 'Suma de ppto', 'Suma de OC', 'Suma de SM', 'Diferencia'];
		const dataRows = sortedRows.map((row) => [row.cuenta, row.ppto, row.oc, row.sm, row.diferencia]);
		const totalRow = ['TOTAL', acumTotals.ppto, acumTotals.oc, acumTotals.sm, acumTotals.ppto - acumTotals.oc - acumTotals.sm];
		const colCount = headers.length;
		const diffColIndex = 4;

		const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows, totalRow]);
		ws['!cols'] = [{ wch: 38 }, ...Array(colCount - 1).fill({ wch: 14 })];

		const totalRowIndex = dataRows.length + 2;

		for (let rowIndex = 2; rowIndex <= totalRowIndex; rowIndex++) {
			for (let colIndex = 1; colIndex <= colCount; colIndex++) {
				const cellRef = XLSX.utils.encode_cell({ r: rowIndex - 1, c: colIndex - 1 });
				if (!ws[cellRef]) continue;
				if (!ws[cellRef].s) ws[cellRef].s = {};

				if (colIndex === 1) {
					ws[cellRef].s.font = { ...(ws[cellRef].s.font || {}), bold: true, color: { rgb: '000000' } };
				} else {
					ws[cellRef].z = '#,##0';
				}
			}

			const diffCellRef = XLSX.utils.encode_cell({ r: rowIndex - 1, c: diffColIndex });
			if (ws[diffCellRef] && Number(ws[diffCellRef].v) < 0) {
				if (!ws[diffCellRef].s) ws[diffCellRef].s = {};
				ws[diffCellRef].s.font = {
					...(ws[diffCellRef].s.font || {}),
					color: { rgb: 'FF0000' },
					bold: rowIndex === totalRowIndex ? true : Boolean(ws[diffCellRef].s.font?.bold)
				};
			}
		}

		styleHeaderAndTotalRow(ws, colCount, totalRowIndex);
		downloadWorkbook(ws);
	};

	const exportBudgExcel = () => {
		if (typeAccum === 'Mensual') {
			exportMensualExcel();
		} else {
			exportAcumuladoExcel();
		}
	};

	return (
		<form>
			<style jsx="true">{`
				.budgettable {
					table-layout: fixed;
				}
				.budgettable td:first-child {
					width: 270px;
				}
				.budgettable td:not(:first-child) {
					width: 60px;
					text-align: right;
				}
				.budgettable td input {
					width: 100%;
				}
				.hiddencost {
					display: grid;
					grid-template-rows: 0fr;
					transition: grid-template-rows 0.25s ease;
					line-height: 20px;
				}
				.hiddencost.open {
					grid-template-rows: 1fr;
				}
			`}</style>
			<main>
				<h1 data-icon=''>Presupuesto</h1>
				{typeAccum === 'Acumulado' ? (
					<div style={{ overflowX: 'auto', maxWidth: '1000px' }}>
						<nav>
							<article>
								<table className='budgettable'>
									<thead>
										<tr>
											<td onClick={() => handleRequestSort('cuenta')}>Cuenta {orderBy === 'cuenta' && (<orm-icon>{order === 'asc' ? '' : ''}</orm-icon>)}</td>
											<td onClick={() => handleRequestSort('ppto')}>Suma de ppto {orderBy === 'ppto' && (<orm-icon>{order === 'asc' ? '' : ''}</orm-icon>)}</td>
											<td onClick={() => handleRequestSort('oc')}>Suma de OC {orderBy === 'oc' && (<orm-icon>{order === 'asc' ? '' : ''}</orm-icon>)}</td>
											<td onClick={() => handleRequestSort('sm')}>Suma de SM {orderBy === 'sm' && (<orm-icon>{order === 'asc' ? '' : ''}</orm-icon>)}</td>
											<td onClick={() => handleRequestSort('diferencia')}>Diferencia {orderBy === 'diferencia' && (<orm-icon>{order === 'asc' ? '' : ''}</orm-icon>)}</td>
										</tr>
									</thead>
									<tbody>
										{sortedRows.map((row, index) => (
											<tr key={index}>
												<td style={{ fontWeight: 'bold' }}>{row.cuenta}</td>
												<td>{fFmt(row.ppto)}</td>
												<td>{fFmt(row.oc)}</td>
												<td>{fFmt(row.sm)}</td>
												<td style={row.diferencia < 0 ? { color: 'red' } : null}>{fFmt(row.diferencia)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</article>
						</nav>
						<nav>
							<article>
								<table className='budgettable'>
									<tbody>
										<tr>
											<td style={{ fontWeight: 'bold' }}>TOTAL</td>
											<td style={{ fontWeight: 'bold' }}>{fFmt(acumTotals.ppto)}</td>
											<td style={{ fontWeight: 'bold' }}>{fFmt(acumTotals.oc)}</td>
											<td style={{ fontWeight: 'bold' }}>{fFmt(acumTotals.sm)}</td>
											<td style={(acumTotals.ppto - acumTotals.oc - acumTotals.sm) < 0 ? { color: 'red', fontWeight: 'bold' } : { fontWeight: 'bold' }}>{fFmt(acumTotals.ppto - acumTotals.oc - acumTotals.sm)}</td>
										</tr>
									</tbody>
								</table>
							</article>
						</nav>
					</div>
				) : (
					<div style={{ overflowX: 'auto', maxWidth: '1000px' }}>
						<BudgMonthly
							monthlyRows={filteredMonthlyRows}
							allMonthlyRows={monthlyRows}
							startDate={startDate}
							endDate={endDate}
							canEdit={Boolean(filtCCCode) && isPrivileged}
							mail={mail}
							cent={filtCCCode}
							onSaved={loadBudgMonthly}
							ocByAccount={ocByAccount}
							smByAccount={smByAccount}
							ocByAccountMonth={ocByAccountMonth}
							smByAccountMonth={smByAccountMonth}
							globalMonthly={globalMonthly}
						/>
					</div>
				)}
			</main>
			<aside>
				<article>
					<h2>Acciones</h2>
					<fieldset>
						<select value={typeAccum} onChange={(e) => setTypeAccum(e.target.value)}>
							<option value='Acumulado'>Acumulado</option>
							<option value='Mensual'>Mensual</option>
						</select>
						<div className='ellipsis' onClick={(e) => {
							e.preventDefault();
							exportBudgExcel();
						}}>
							<button></button> <label id='copyLabel'>Exportar Planilla</label>
						</div>
					</fieldset>
				</article>
				<article>
					<h2>Filtros</h2>
					<fieldset>
						<legend>Presupuestado</legend>
						<input type='search' placeholder='Centro de costo' list='cent' value={filtCC} onChange={(e) => setFiltCC(e.target.value)} />
						<datalist id='cent'>
							{listCC.map((cc) => (
								<option key={cc.code} value={cc.code}>{cc.name}</option>
							))}
						</datalist>
						<input type='search' placeholder='Cuenta' list='accounts' value={filtCuenta} onChange={(e) => setFiltCuenta(e.target.value)} />
						<datalist id='accounts'>
						{listAcco.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((a) => (
								<option key={a.code} value={a.code}>{a.name}</option>
							))}
						</datalist>
						<input type='month' value={startDate} onChange={(e) => setStartDate(e.target.value)} />
						<input type='month' value={endDate} onChange={(e) => setEndDate(e.target.value)} />
					</fieldset>
				</article>
			</aside>
		</form>
	);
}

function BudgMonthly({ monthlyRows, allMonthlyRows, startDate, endDate, canEdit, mail, cent, onSaved, ocByAccount = {}, smByAccount = {}, ocByAccountMonth = {}, smByAccountMonth = {}, globalMonthly = {} }) {
	const [editingCell, setEditingCell] = useState(null);
	const [editingValue, setEditingValue] = useState('');
	const [saving, setSaving] = useState(false);
	const [recentlyAdded, setRecentlyAdded] = useState([]);
	const [expandedRows, setExpandedRows] = useState([]);
	const [totalExpanded, setTotalExpanded] = useState(false);

	const toggleExpandedRow = (acco) => {
		setExpandedRows((prev) => prev.includes(acco) ? prev.filter((code) => code !== acco) : [...prev, acco]);
	};

	const handleAccountAdded = (acco) => {
		setRecentlyAdded((prev) => [...prev.filter((code) => code !== acco), acco]);
	};

	const fNum = (value) => Number(value || 0);
	const fCellZero = (value) => fNum(value).toLocaleString('es-CL');

	const monthColumns = getMonthColumns(startDate, endDate, allMonthlyRows || monthlyRows);

	const handleEditStart = (row, col) => {
		if (!canEdit || saving) return;
		setEditingCell({ cuenta: row.cuenta, key: col.key });
		setEditingValue(row.values[col.key] ?? '');
	};

	const handleEditEnd = async (row, col) => {
		setEditingCell(null);
		if (!mail || !cent || !row.acco) return;

		const isEmpty = editingValue === '' || editingValue === null || editingValue === undefined;

		if (isEmpty) {
			if (row.values[col.key] === undefined) return;

			setSaving(true);
			try {
				const query = `DELETE FROM inv__budg WHERE acco=${Number(row.acco)} AND cent=${Number(cent)} AND year=${col.year} AND mont=${col.month}`;

				await fetch(apiurl + 'queryAux', {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ user: mail, query })
				});

				await onSaved?.();
			} catch (error) {
				console.error('Error eliminando presupuesto mensual:', error);
			} finally {
				setSaving(false);
			}
			return;
		}

		const value = Number(editingValue || 0);
		if (value === Number(row.values[col.key] || 0)) return;

		setSaving(true);
		try {
			const query = `INSERT INTO inv__budg (acco, cent, year, mont, budg) VALUES (${Number(row.acco)}, ${Number(cent)}, ${col.year}, ${col.month}, ${value})
				ON DUPLICATE KEY UPDATE budg=${value}`;

			await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ user: mail, query })
			});

			await onSaved?.();
		} catch (error) {
			console.error('Error guardando presupuesto mensual:', error);
		} finally {
			setSaving(false);
		}
	};

	const filteredMonthlyRows = monthlyRows.map((row) => {
		const rowTotal = monthColumns.reduce((acc, col) => acc + Number(row.values[col.key] || 0), 0);
		return { ...row, rowTotal };
	});

	const monthlyTotals = monthColumns.map((col) => Number(globalMonthly[col.key]?.ppto || 0));

	const monthlyOcTotals = monthColumns.map((col) => Number(globalMonthly[col.key]?.oc || 0) * -1);
	const monthlySmTotals = monthColumns.map((col) => Number(globalMonthly[col.key]?.sm || 0) * -1);
	const monthlyTotalTotals = monthlyTotals.map((total, index) => total + monthlyOcTotals[index] + monthlySmTotals[index]);

	const monthlyGrandTotal = monthlyTotals.reduce((acc, val) => acc + val, 0);
	const ocGrandTotal = monthlyOcTotals.reduce((acc, val) => acc + val, 0);
	const smGrandTotal = monthlySmTotals.reduce((acc, val) => acc + val, 0);
	const monthlyGrandTotalTotal = monthlyGrandTotal + ocGrandTotal + smGrandTotal;

	const orderedMonthlyRows = [...filteredMonthlyRows].sort((a, b) => {
		const aIndex = recentlyAdded.indexOf(a.acco);
		const bIndex = recentlyAdded.indexOf(b.acco);
		if (aIndex === -1 && bIndex === -1) return 0;
		if (aIndex === -1) return -1;
		if (bIndex === -1) return 1;
		return aIndex - bIndex;
	});

	return (
		<>
			<nav>
				<article>
					<table className='budgettable'>
						<thead>
							<tr>
								<td>Cuenta</td>
								{monthColumns.map((col) => (
									<td key={col.key}>{col.label}</td>
								))}
								<td>TOTAL</td>
							</tr>
						</thead>
						<tbody>
							{orderedMonthlyRows.map((row, index) => {
								const isExpanded = expandedRows.includes(row.acco);
								const oc = Number(ocByAccount[String(row.acco)] || 0);
								const sm = Number(smByAccount[String(row.acco)] || 0);
								return (
									<tr key={index}>
										<td style={{ fontWeight: 'bold', cursor: 'pointer' }} onClick={() => toggleExpandedRow(row.acco)}>
											<orm-icon>{isExpanded ? '' : ''}</orm-icon> {row.cuenta}
											<div className={`hiddencost ${isExpanded ? 'open' : ''}`}>
												<div style={{ overflow: 'hidden', fontWeight: 'normal' }}>
													<div>OC</div>
													<div>SM</div>
													<div style={{ fontWeight: 'bold' }}>Total</div>
												</div>
											</div>
										</td>
										{monthColumns.map((col) => {
											const isEditing = editingCell?.cuenta === row.cuenta && editingCell?.key === col.key;
											const ocMonth = Number(ocByAccountMonth[String(row.acco)]?.[col.key] || 0) * -1;
											const smMonth = Number(smByAccountMonth[String(row.acco)]?.[col.key] || 0) * -1;
											const totalMonth = Number(row.values[col.key] || 0) + ocMonth + smMonth;
											const showExpanded = isExpanded && !isEditing;
											return (
												<td
													key={col.key}
													style={{ verticalAlign: 'top' }}
													onClick={() => handleEditStart(row, col)}
												>
													<span className={canEdit && !isEditing ? "editable": ""}>
														{isEditing ? (
															<input
																type='number'
																autoFocus
																value={editingValue}
																onChange={(e) => setEditingValue(e.target.value)}
																onBlur={() => handleEditEnd(row, col)}
															/>
														) : (
															fCellZero(row.values[col.key])
														)}
													</span>
													<div className={`hiddencost ${showExpanded ? 'open' : ''}`}>
														<div style={{ overflow: 'hidden' }}>
															<div>{fCellZero(ocMonth)}</div>
															<div>{fCellZero(smMonth)}</div>
															<div style={totalMonth < 0 ? { color: 'red', fontWeight: 'bold' } : { fontWeight: 'bold' }}>{fCellZero(totalMonth)}</div>
														</div>
													</div>
												</td>
											);
										})}
										<td style={{ fontWeight: 'bold' }}>
											{fCellZero(row.rowTotal)}
											<div className={`hiddencost ${isExpanded ? 'open' : ''}`}>
												<div style={{ overflow: 'hidden', fontWeight: 'normal' }}>
													<div>{fCellZero(oc* -1)}</div>
													<div>{fCellZero(sm* -1)}</div>
													<div style={(row.rowTotal + oc * -1 + sm * -1) < 0 ? { color: 'red', fontWeight: 'bold' } : { fontWeight: 'bold' }}>{fCellZero((row.rowTotal + oc* -1 + sm* -1))}</div>
												</div>
											</div>
										</td>
									</tr>
								);
							})}
							{canEdit && (
								<AddBudgRow
									monthColumns={monthColumns}
									monthlyRows={monthlyRows}
									canEdit={canEdit}
									mail={mail}
									cent={cent}
									onSaved={onSaved}
									onAccountAdded={handleAccountAdded}
								/>
							)}
						</tbody>
					</table>
				</article>
			</nav>
			<nav>
				<article>
					<table className='budgettable'>
						<tbody>
							<tr>
								<td style={{ fontWeight: 'bold', cursor: 'pointer', verticalAlign: 'top' }} onClick={() => setTotalExpanded((prev) => !prev)}>
									<orm-icon>{totalExpanded ? '' : ''}</orm-icon> TOTAL
									<div className={`hiddencost ${totalExpanded ? 'open' : ''}`}>
										<div style={{ overflow: 'hidden', fontWeight: 'normal' }}>
											<div>OC</div>
											<div>SM</div>
											<div style={{ fontWeight: 'bold' }}>Total</div>
										</div>
									</div>
								</td>
								{monthlyTotals.map((total, index) => (
									<td key={monthColumns[index]?.key ?? index} style={{ fontWeight: 'bold', verticalAlign: 'top' }}>
										{fCellZero(total)}
										<div className={`hiddencost ${totalExpanded ? 'open' : ''}`}>
											<div style={{ overflow: 'hidden', fontWeight: 'normal' }}>
												<div>{fCellZero(monthlyOcTotals[index])}</div>
												<div>{fCellZero(monthlySmTotals[index])}</div>
												<div style={monthlyTotalTotals[index] < 0 ? { color: 'red', fontWeight: 'bold' } : { fontWeight: 'bold' }}>{fCellZero(monthlyTotalTotals[index])}</div>
											</div>
										</div>
									</td>
								))}
								<td style={{ fontWeight: 'bold', verticalAlign: 'top' }}>
									{fCellZero(monthlyGrandTotal)}
									<div className={`hiddencost ${totalExpanded ? 'open' : ''}`}>
										<div style={{ overflow: 'hidden', fontWeight: 'normal' }}>
											<div>{fCellZero(ocGrandTotal)}</div>
											<div>{fCellZero(smGrandTotal)}</div>
											<div style={monthlyGrandTotalTotal < 0 ? { color: 'red', fontWeight: 'bold' } : { fontWeight: 'bold' }}>{fCellZero(monthlyGrandTotalTotal)}</div>
										</div>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</article>
			</nav>
		</>
	);
}

function AddBudgRow({ monthColumns, monthlyRows, canEdit, mail, cent, onSaved, onAccountAdded }) {
	const [accoList, setAccoList] = useState([]);
	const [selectedAcco, setSelectedAcco] = useState('');
	const [editingCell, setEditingCell] = useState(null);
	const [editingValue, setEditingValue] = useState('');
	const [saving, setSaving] = useState(false);

	const fNum = (value) => Number(value || 0);

	useEffect(() => {
		if (!mail) return;

		const loadAcco = async () => {
			try {
				const response = await fetch(apiurl, {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ table: 'inv__acco', order: 'name', user: mail })
				});

				if (response.ok) {
					const data = await response.json();
					setAccoList(data.map((row) => ({ code: row.code, name: row.name })));
				} else {
					console.error('Error al obtener las cuentas');
				}
			} catch (error) {
				console.error('Error en loadAcco:', error);
			}
		};

		loadAcco();
	}, [mail]);

	const selectedRow = monthlyRows.find((row) => String(row.acco) === String(selectedAcco));
	const rowValues = selectedRow?.values || {};

	const handleEditStart = (col) => {
		if (!canEdit || !selectedAcco || saving) return;
		setEditingCell(col.key);
		setEditingValue(rowValues[col.key] ?? '');
	};

	const handleEditEnd = async (col) => {
		setEditingCell(null);
		if (!mail || !cent || !selectedAcco) return;

		const value = Number(editingValue || 0);
		if (value === Number(rowValues[col.key] || 0)) return;

		setSaving(true);
		try {
			const checkResponse = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: `SELECT COUNT(*) AS cnt FROM inv__budg WHERE acco=${Number(selectedAcco)} AND cent=${Number(cent)} AND year=${col.year} AND mont=${col.month}`
				})
			});
			const checkData = await checkResponse.json();
			const exists = Number(checkData?.[0]?.cnt || 0) > 0;

			if (exists) {
				alert('Ya existe un presupuesto cargado para esa cuenta, mes y centro de costo.');
				return;
			}

			const query = `INSERT INTO inv__budg (acco, cent, year, mont, budg) VALUES (${Number(selectedAcco)}, ${Number(cent)}, ${col.year}, ${col.month}, ${value})`;

			await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ user: mail, query })
			});

			onAccountAdded?.(Number(selectedAcco));
			await onSaved?.();
			setSelectedAcco('');
			setEditingValue('');
		} catch (error) {
			console.error('Error guardando presupuesto mensual:', error);
		} finally {
			setSaving(false);
		}
	};

	const sortedAcco = [...accoList].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

	return (
		<tr>
			<td>
				<figure className='blu' style={{ marginRight: '5px' }}></figure>
				<select style={{ maxWidth: '130px' }} value={selectedAcco} onChange={(e) => setSelectedAcco(e.target.value)}>
					<option value=''>Agregar</option>
					{sortedAcco.map((acco) => (
						<option key={acco.code} value={acco.code}>{acco.name}</option>
					))}
				</select>
			</td>
			{monthColumns.map((col) => {
				const isEditing = editingCell === col.key;
				return (
					<td
						key={col.key}
						className={canEdit && selectedAcco && !isEditing ? 'editable' : ''}
						onClick={() => handleEditStart(col)}
					>
						{isEditing ? (
							<input
								type='number'
								autoFocus
								value={editingValue}
								onChange={(e) => setEditingValue(e.target.value)}
								onBlur={() => handleEditEnd(col)}
							/>
						) : (
							fNum(rowValues[col.key]).toLocaleString('es-CL')
						)}
					</td>
				);
			})}
			<td></td>
		</tr>
	);
}

export default ListBudg;
